import { format, getISOWeek, isSameDay } from 'date-fns';
import { t } from 'i18next';

/**
 * Kalenderwoche label for a date ("Sett. 35"), or a collapsed range label when
 * the period spans several ISO weeks ("Sett. 35–38"). Weeks are ISO 8601, i.e.
 * Monday-based, matching the { weekStartsOn: 1 } used across the app.
 */
export const weekLabel = (start: Date, end?: Date): string => {
    const from = getISOWeek(start);
    const to = end ? getISOWeek(end) : from;
    return from === to
        ? t('shift.weekShort', { n: from })
        : `${t('shift.weekShort', { n: from })}–${to}`;
};

/**
 * Whether a day should carry the week label in a day-by-day view: true on
 * Mondays and on the first day of the period, so the number appears once per
 * week instead of on every column.
 */
export const startsWeek = (day: Date, periodStart: Date): boolean =>
    day.getDay() === 1 || isSameDay(day, periodStart);

export interface WeekBand {
    /** Stable key: the ISO date of the band's first day. */
    key: string;
    week: number;
    label: string;
    days: Date[];
}

/**
 * Splits an ordered list of days into contiguous runs belonging to the same ISO
 * week, so a header can draw one labelled band spanning each week's columns.
 */
export const groupByWeek = (days: Date[]): WeekBand[] =>
    days.reduce<WeekBand[]>((bands, day) => {
        const week = getISOWeek(day);
        const last = bands[bands.length - 1];
        if (last && last.week === week) {
            last.days.push(day);
        } else {
            bands.push({ key: format(day, 'yyyy-MM-dd'), week, label: weekLabel(day), days: [day] });
        }
        return bands;
    }, []);
